import React from 'react';
import {Box, Typography} from '@mui/material';
import {useHistory} from 'react-router-dom';
import {useAuth} from '../../../context/auth';
import {getUserDetailsService, updateUserDetailsService} from '../../../services/auth.service';
import {planHasSequentialSteps, mergeOnboardingRefreshIntoAuthUser} from '../../DQPro/DQProV2/constants/onboardingSteps.config';
import ScopeSuppressedBanner from '../../DashboardCommon/ScopeSuppressedBanner.comp';

/**
 * Shared page shell for every Partner Dashboard section.
 * Renders the page header (title + Back to Workspace button), optional tabs, and {children}.
 */
const PartnerDashboardShell = ({tabs, children}) => {
    const history = useHistory();
    const {authUser, setAuthUser} = useAuth();

    // Onboarding: viewing the partner dashboard is the completion signal for
    // the OWNER's Step 3 ("Track" card → /dq_pro/partner-dashboard/overview) —
    // parallels SchoolDashboard's write for the level-22 client. Without this
    // the owner's Step 4 never unlocks: the older step-3 write lives in
    // ImpactDashboard, which the P0 Step 3 card doesn't navigate to.
    React.useEffect(() => {
        if (!planHasSequentialSteps(authUser?.dq_pro_plan?.plan_key)) return;
        updateUserDetailsService({complete_onboarding_step: 3})
            .then(() => getUserDetailsService())
            .then(res => {
                const refreshedUser = res?.data?.user || res?.data;
                // Merge ONLY onboarding progress — a full replace drops
                // login-time session fields (see mergeOnboardingRefreshIntoAuthUser).
                const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                if (merged !== authUser) setAuthUser(merged);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            {/* Page header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 2,
                pb: 2.5,
                borderBottom: '1px solid #E5E7EB',
                flexWrap: 'wrap',
                gap: 2,
            }}>
                <Box>
                    <Typography sx={{
                        fontSize: {xs: '20px', md: '26px'},
                        fontWeight: 800,
                        color: '#052F44',
                        lineHeight: 1.2,
                    }}>
                        Partner Dashboard
                    </Typography>
                    <Typography sx={{fontSize: '14px', color: '#6A7282', mt: 0.5}}>
                        Child online safety readiness, cyber risk and support environment for students
                    </Typography>
                </Box>
            </Box>

            {/* Scores hidden when the partner's own population is under the n floor */}
            <ScopeSuppressedBanner/>

            {/* Persistent Tabs Navigation - rendered above content */}
            {tabs}

            {/* Section content rendered unconditionally so child pages/tabs run their own lifecycle */}
            {children}
        </Box>
    );
};

export default PartnerDashboardShell;
